import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { BillTriageAPI } from '../../../api';
import { State } from '../../../dux/@types';
import { useToast } from '../Toast/ToastProvider';

const billCountLabel = (billCount: number): string =>
  billCount === 1 ? 'this bill' : `${billCount} bills`;

const markedReadMessage = (billCount: number, leftUpdatedBills: boolean): string => {
  const removedSentence =
    billCount === 1
      ? " It's been removed from Updated Bills."
      : " They've been removed from Updated Bills.";
  return `You've marked ${billCountLabel(billCount)} as read.${
    leftUpdatedBills ? removedSentence : ''
  }`;
};

type MarkBillsAsReadParams = {
  orgId: number;
  billIds: string[];
};

// Marks bills' updates as read and shows a toast that can undo it. Resolves to whether the
// bills were marked. Requests are dispatched directly because the rows usually leave
// Updated Bills and unmount before undo is clicked.
const useMarkBillsAsRead = (): ((
  params: MarkBillsAsReadParams,
) => Promise<boolean>) => {
  const dispatch = useDispatch<ThunkDispatch<State, unknown, AnyAction>>();
  const { showToast } = useToast();

  const undo = (orgId: number, billIds: string[], seenStateIds: number[]): void => {
    const label = billCountLabel(billIds.length);
    void dispatch(
      BillTriageAPI.endpoints.undoMarkBillUpdatesSeen.initiate(
        { orgId, billIds, seenStateIds },
        { track: false },
      ),
    )
      .unwrap()
      .then(
        () => showToast({ message: `Marked ${label} as unread.` }),
        () =>
          showToast({
            message: `We couldn't mark ${label} as unread.`,
            variant: 'error',
          }),
      );
  };

  return ({ orgId, billIds }) =>
    dispatch(
      BillTriageAPI.endpoints.markAllBillUpdatesSeen.initiate(
        { orgId, billIds },
        { track: false },
      ),
    )
      .unwrap()
      .then(
        ({ seenStateIds, leftUpdatedBills }) => {
          showToast({
            message: markedReadMessage(billIds.length, leftUpdatedBills),
            onUndo: seenStateIds.length
              ? () => undo(orgId, billIds, seenStateIds)
              : undefined,
          });
          return true;
        },
        () => {
          showToast({
            message: `We couldn't mark ${billCountLabel(billIds.length)} as read.`,
            variant: 'error',
          });
          return false;
        },
      );
};

export default useMarkBillsAsRead;
